import { useState } from "react";
import { useTranslation } from "react-i18next";
import { useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, InlineNotification, Loading } from "@carbon/react";
import Form from "../carbon/Form/Form";
import TextField from "../carbon/Form/TextField";
import PageHeader from "../carbon/PageHeader/PageHeader";
import ConfirmDialog from "../carbon/ConfirmDialog/ConfirmDialog";
import { newSupplierValidationSchema } from "../../utils";
import { useUploadSupplier } from "../../hooks/useSuppliers";
import { useNotification } from "../../context";
import { SupplierRequest } from "../../types";
import "./newSupplierForm.scss";

type NewSupplierFormProps = {
  /** When provided (the `showAsModal` embed's only current caller, `InviteSupplier.tsx`), a
   * validated submission is handed to this instead of running the default `useUploadSupplier`
   * mutation — the caller owns success/error handling and closing its own panel. */
  onSupplierCreated?: (data: SupplierRequest) => void;
  /** Renders the bare field set with no page chrome (`PageHeader`/`.cds-form-surface`) and no
   * confirm-dialog step, for embedding inside another surface (`InviteSupplier.tsx`'s own bordered
   * panel). Default `false` renders the standalone `/suppliers/new` page. */
  showAsModal?: boolean;
  /** Called instead of navigating to `/suppliers` on cancel — the modal embed's way of closing
   * itself instead of leaving the page it's embedded in. */
  onCancel?: () => void;
};

const defaultValues: SupplierRequest = {
  companyName: "",
  cuit: "",
  email: "",
  firstName: "",
  lastName: "",
};

/**
 * The create-supplier form (`POST /suppliers/upload`), rebuilt on the Carbon form primitives
 * (Task 6, Suppliers flow) in place of 276 lines of Tailwind + legacy `InputField` + shared
 * `Modal`/`Button` — the same `Form`/`TextField` + RHF `control` wiring `NewSiteForm.tsx`/
 * `NewUserForm.tsx` already establish.
 *
 * TWO RENDER PATHS, both preserved from the pre-Carbon version:
 *
 * 1. STANDALONE `/suppliers/new` (`showAsModal` falsy, the default): a `PageHeader` + a
 *    `.cds-form-surface` panel, exactly like every other migrated "new X" form. Submitting first
 *    runs Yup via `Form`'s own `handleSubmit` — only a value that already validated ever opens
 *    `ConfirmDialog`, the same "no confirming an empty form" fix `NewSiteForm.tsx`'s/
 *    `NewUserForm.tsx`'s own docstrings describe (the pre-Carbon version opened its confirmation
 *    `Modal` unconditionally on click and only validated once THAT modal's own button was
 *    pressed).
 * 2. EMBEDDED MODAL (`showAsModal={true}`) — `InviteSupplier.tsx`'s only consumer
 *    (`src/components/Material/InviteSupplier/InviteSupplier.tsx`, passing `onSupplierCreated`,
 *    `showAsModal`, `onCancel`): the bare field set with no page chrome, sitting inside
 *    `InviteSupplier`'s own bordered panel. This path submits directly on a valid `Form` submit —
 *    no `ConfirmDialog` step — matching the pre-Carbon version's own modal branch, which called
 *    `handleSubmit(onSubmit, onError)` straight from its Add button with no intermediate
 *    confirmation UI.
 *
 * `onSupplierCreated`/`showAsModal`/`onCancel` are the exact prop contract the pre-Carbon version
 * exposed — kept unchanged so `InviteSupplier.tsx` (Materials/invite territory, out of this task's
 * scope) needs no changes at all.
 */
const NewSupplierForm = ({
  onSupplierCreated,
  showAsModal = false,
  onCancel,
}: NewSupplierFormProps = {}) => {
  const { openNotification } = useNotification();
  const { t } = useTranslation();
  const navigate = useNavigate();

  // Only used by the standalone path — see docstring point 1. The embedded-modal path submits
  // straight through `submit` below and never sets this.
  const [pendingValues, setPendingValues] = useState<SupplierRequest | null>(
    null
  );

  const { control, handleSubmit } = useForm<SupplierRequest>({
    resolver: yupResolver(newSupplierValidationSchema),
    defaultValues,
  });

  const {
    mutate: createSupplier,
    isPending: isCreatingSupplier,
    isError,
  } = useUploadSupplier();

  // Shared by both paths: `onSupplierCreated`, when provided, always wins over the default
  // `useUploadSupplier` mutation — identical branch to the pre-Carbon version's own `onSubmit`.
  const submit = (data: SupplierRequest) => {
    if (onSupplierCreated) {
      onSupplierCreated(data);
      setPendingValues(null);
      return;
    }
    createSupplier(data, {
      onSuccess: () => {
        openNotification(t("suppliers.success"));
        setPendingValues(null);
        navigate("/suppliers");
      },
      onError: () => {
        setPendingValues(null);
        openNotification(t("newMaterialForm.error"));
      },
    });
  };

  const handleCancel = () => {
    if (onCancel) {
      onCancel();
    } else {
      navigate("/suppliers");
    }
  };

  const handleConfirm = () => {
    if (!pendingValues) return;
    submit(pendingValues);
  };

  if (isCreatingSupplier) {
    return <Loading withOverlay description={t("cta.loading")} />;
  }

  const errorNotification = isError ? (
    <InlineNotification
      kind="error"
      lowContrast
      hideCloseButton
      title={t("suppliers.emailExists")}
      className="new-supplier-form__notification"
    />
  ) : null;

  const fields = (
    <div className="new-supplier-form__grid">
      <TextField
        name="companyName"
        label={t("suppliers.companyName")}
        control={control}
        required
      />
      <TextField
        name="cuit"
        label={t("suppliers.taxId")}
        control={control}
        required
      />
      <TextField
        name="email"
        type="email"
        label={t("suppliers.email")}
        control={control}
        required
      />
      <TextField
        name="firstName"
        label={t("suppliers.firstName")}
        control={control}
        optional
      />
      <TextField
        name="lastName"
        label={t("suppliers.lastName")}
        control={control}
        optional
      />
    </div>
  );

  if (showAsModal) {
    return (
      <div className="new-supplier-form">
        {errorNotification}
        <Form
          handleSubmit={handleSubmit}
          onSubmit={submit}
          noValidate
          className="new-supplier-form__form"
        >
          {fields}
          <div className="new-supplier-form__actions">
            <Button kind="secondary" onClick={handleCancel}>
              {t("cta.cancel")}
            </Button>
            <Button type="submit">{t("cta.add")}</Button>
          </div>
        </Form>
      </div>
    );
  }

  return (
    <div>
      <PageHeader title={t("suppliers.add")} />
      {errorNotification}
      <Form
        handleSubmit={handleSubmit}
        onSubmit={(data) => setPendingValues(data)}
        noValidate
        className="new-supplier-form__form cds-form-surface"
      >
        {fields}
        <div className="new-supplier-form__actions">
          <Button kind="secondary" onClick={handleCancel}>
            {t("cta.cancel")}
          </Button>
          <Button type="submit">{t("cta.add")}</Button>
        </div>
      </Form>

      <ConfirmDialog
        open={pendingValues !== null}
        title={t("suppliers.add")}
        body={t("suppliers.addQuestion")}
        confirmLabel={t("cta.add")}
        onConfirm={handleConfirm}
        onCancel={() => setPendingValues(null)}
        isPending={isCreatingSupplier}
      />
    </div>
  );
};

export default NewSupplierForm;
